import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { getStripeEnvironment } from '@/lib/stripe';

interface AddFacilityConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  facilityName: string;
  /** Called when the user confirms — parent should perform the create. */
  onConfirm: () => void | Promise<void>;
  isWorking?: boolean;
}

/**
 * Shows the prorated cost the new facility will add to Stripe before the user
 * commits. Calls update-module-quantity in preview mode to compute the figure.
 */
export function AddFacilityConfirmDialog({
  open,
  onOpenChange,
  facilityName,
  onConfirm,
  isWorking,
}: AddFacilityConfirmDialogProps) {
  const [previewCents, setPreviewCents] = useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewCents(null);
    (async () => {
      try {
        const environment = (() => {
          try {
            return getStripeEnvironment();
          } catch {
            return 'sandbox' as const;
          }
        })();
        // Ask the backend to project the prorated charge for current_count + 1.
        const { count } = await supabase
          .from('sites')
          .select('id', { count: 'exact', head: true })
          .eq('is_active', true);
        const projected = (count ?? 0) + 1;
        const { data, error } = await supabase.functions.invoke(
          'update-module-quantity',
          {
            body: { environment, preview: true, quantity: projected },
          },
        );
        if (cancelled) return;
        if (error) throw error;
        setPreviewCents(data?.total_preview_amount_cents ?? 0);
      } catch (e: any) {
        if (cancelled) return;
        setPreviewError(e?.message || 'Could not compute cost preview');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const formatCurrency = (cents: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
      cents / 100,
    );

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Add facility "{facilityName}"?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>
                Adding a facility increases your active subscription quantity. A
                prorated charge will be applied to your current billing period.
              </p>
              <div className="rounded-md border bg-muted/40 p-3">
                {previewLoading && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Calculating prorated charge…
                  </div>
                )}
                {!previewLoading && previewError && (
                  <p className="text-amber-700">
                    Could not preview cost: {previewError}. Continuing will still
                    sync billing on the next renewal.
                  </p>
                )}
                {!previewLoading && previewError === null && previewCents !== null && (
                  <p>
                    Estimated prorated charge today:{' '}
                    <strong>{formatCurrency(previewCents)}</strong>
                  </p>
                )}
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isWorking}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={isWorking}
            onClick={(e) => {
              e.preventDefault();
              void onConfirm();
            }}
          >
            {isWorking ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Adding…
              </>
            ) : (
              'Confirm and add'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
